import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Modal, Select, Spin, message } from 'antd';
import { useTranslation } from '../../../i18n';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import {
  createEmailCampaign,
  getContentTemplates,
  getInfluencerCollectionPage,
  getInfluencerPage
} from '../../../store/request';
import type { InfluencerCollectionRecord, InfluencerRecord } from '../../../types/influencer';
import type { TemplateListItem } from '../../../types/template';

type TalentTab = 'mine' | 'square';

type PickerTalent = {
  id: string;
  influencerId: string;
  creatorOpenId: string;
  name: string;
  handle: string;
  email: string;
  avatarUrl: string;
  initial: string;
  meta: string;
};

function toPickerTalent(row: {
  influencerId?: string;
  creatorOpenId?: string;
  nickname?: string;
  username?: string;
  email?: string;
  avatarUrl?: string;
  selectionRegion?: string;
  categoryNames?: string[];
  isBlacklist?: boolean;
  status?: number;
}): PickerTalent | null {
  if (row.isBlacklist || row.status === 3) return null;
  const influencerId = String(row.influencerId ?? '').trim();
  const creatorOpenId = String(row.creatorOpenId ?? '').trim();
  const id = influencerId || creatorOpenId;
  if (!id) return null;
  const name = row.nickname?.trim() || row.username?.trim() || id;
  const handle = row.username?.replace(/^@/, '').trim() || '';
  const email = row.email?.trim() || '';
  const categories = row.categoryNames?.filter(Boolean).slice(0, 2).join(' · ') ?? '';
  return {
    id,
    influencerId,
    creatorOpenId,
    name,
    handle,
    email,
    avatarUrl: row.avatarUrl?.trim() || '',
    initial: name.slice(0, 1).toUpperCase() || '?',
    meta: [handle ? `@${handle}` : '', email, row.selectionRegion, categories].filter(Boolean).join(' · ') || '—'
  };
}

type NewEmailModalProps = {
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
};

export function NewEmailModal({ open, onClose, onSent }: NewEmailModalProps) {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const { shopId } = useSelectedShop();
  const [tab, setTab] = useState<TalentTab>('mine');
  const [keyword, setKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pickedTalents, setPickedTalents] = useState<PickerTalent[]>([]);
  const [mineRecords, setMineRecords] = useState<InfluencerCollectionRecord[]>([]);
  const [mineLoading, setMineLoading] = useState(false);
  const [squareRecords, setSquareRecords] = useState<InfluencerRecord[]>([]);
  const [squareLoading, setSquareLoading] = useState(false);
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateId, setTemplateId] = useState<number>();
  const [taskName, setTaskName] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab('mine');
    setKeyword('');
    setSelectedIds([]);
    setPickedTalents([]);
    setSquareRecords([]);
    setTemplateId(undefined);
    setTaskName('');
  }, [open]);

  const mineKeyword = tab === 'mine' ? keyword.trim() : '';
  const squareKeyword = tab === 'square' ? keyword.trim() : '';

  useEffect(() => {
    if (!open) return;
    const userCode = account?.userCode?.trim();
    if (!userCode) {
      setMineRecords([]);
      setMineLoading(false);
      return;
    }
    let cancelled = false;
    setMineLoading(true);
    const timer = window.setTimeout(() => {
      getInfluencerCollectionPage({
        userCode,
        pageNum: 1,
        pageSize: 50,
        ...(mineKeyword ? { keyword: mineKeyword } : {})
      })
        .then((res) => {
          if (cancelled) return;
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('creator.load_failed'));
            setMineRecords([]);
            return;
          }
          const records = res.content?.records;
          setMineRecords(Array.isArray(records) ? records : []);
        })
        .catch(() => {
          if (!cancelled) message.error(t('creator.load_failed'));
        })
        .finally(() => {
          if (!cancelled) setMineLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, account?.userCode, mineKeyword, t]);

  useEffect(() => {
    if (!open || tab !== 'square') return;
    let cancelled = false;
    setSquareLoading(true);
    const timer = window.setTimeout(() => {
      getInfluencerPage({
        pageNum: 1,
        pageSize: 50,
        ...(squareKeyword ? { keyword: squareKeyword } : {})
      })
        .then((res) => {
          if (cancelled) return;
          if (res.code !== 200) {
            if (res.code !== -1) message.error(res.message || t('talent.load_failed'));
            setSquareRecords([]);
            return;
          }
          const records = res.content?.records;
          setSquareRecords(Array.isArray(records) ? records : []);
        })
        .catch(() => {
          if (!cancelled) message.error(t('talent.load_failed'));
        })
        .finally(() => {
          if (!cancelled) setSquareLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, tab, squareKeyword, t]);

  useEffect(() => {
    if (!open) return;
    const currentShopId = shopId?.trim();
    if (!currentShopId) {
      setTemplates([]);
      setTemplatesLoading(false);
      return;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    getContentTemplates({ shopId: currentShopId, channel: 'EMAIL', pageNum: 1, pageSize: 100 })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          if (res.code !== -1) message.error(res.message || t('invitation.template_load_failed'));
          setTemplates([]);
          return;
        }
        const list = res.content?.records;
        setTemplates(Array.isArray(list) ? list.filter((item) => item.id != null) : []);
      })
      .catch(() => {
        if (!cancelled) message.error(t('invitation.template_load_failed'));
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, shopId, t]);

  const mineTalents = useMemo(
    () => mineRecords.map(toPickerTalent).filter((item): item is PickerTalent => item != null),
    [mineRecords]
  );
  const squareTalents = useMemo(
    () => squareRecords.map(toPickerTalent).filter((item): item is PickerTalent => item != null),
    [squareRecords]
  );
  const talentById = useMemo(() => {
    const map = new Map<string, PickerTalent>();
    pickedTalents.forEach((item) => map.set(item.id, item));
    mineTalents.forEach((item) => map.set(item.id, item));
    squareTalents.forEach((item) => map.set(item.id, item));
    return map;
  }, [pickedTalents, mineTalents, squareTalents]);
  const listSource = tab === 'mine' ? mineTalents : squareTalents;
  const listLoading = tab === 'mine' ? mineLoading : squareLoading;

  const toggleTalent = (talent: PickerTalent, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? (prev.includes(talent.id) ? prev : [...prev, talent.id]) : prev.filter((id) => id !== talent.id)
    );
    setPickedTalents((prev) =>
      checked
        ? prev.some((item) => item.id === talent.id)
          ? prev
          : [...prev, talent]
        : prev.filter((item) => item.id !== talent.id)
    );
  };

  const submit = async () => {
    if (sending) return;
    const currentShopId = shopId?.trim();
    if (!currentShopId) {
      message.warning(t('email.shop_required'));
      return;
    }
    if (selectedIds.length === 0) {
      message.warning(t('invitation.pick_talent'));
      return;
    }
    if (templateId == null) {
      message.warning(t('invitation.template_placeholder'));
      return;
    }
    const template = templates.find((item) => item.id === templateId);
    const subjectText = template?.name?.trim() || '—';
    const body = template?.body?.trim() || '';
    const chosen = selectedIds
      .map((id) => talentById.get(id))
      .filter((item): item is PickerTalent => item != null);
    const recipients = chosen
      .filter((item) => item.email && item.influencerId)
      .map((item) => ({
        influencerId: item.influencerId,
        email: item.email,
        ...(item.name.trim() ? { name: item.name.trim() } : {}),
        ...(item.handle ? { handle: item.handle } : {})
      }));
    if (recipients.length === 0) {
      message.warning(t('invitation.campaign_no_email'));
      return;
    }
    const skipped = chosen.length - recipients.length;
    if (skipped > 0) {
      message.warning(t('invitation.campaign_skipped_email', { count: skipped }));
    }
    const name = taskName.trim();
    setSending(true);
    try {
      const res = await createEmailCampaign({
        shopId: currentShopId,
        subject: subjectText,
        ...(body ? { bodyText: body } : {}),
        recipients,
        ...(name ? { name: name.slice(0, 255) } : {}),
        templateId
      });
      if (res.code !== 200) {
        if (res.code !== -1) message.error(res.message || t('invitation.campaign_failed'));
        return;
      }
      message.success(res.message || t('invitation.campaign_created'));
      onSent?.();
      onClose();
    } catch {
      message.error(t('invitation.campaign_failed'));
    } finally {
      setSending(false);
    }
  };

  const tabClass = (active: boolean) =>
    active
      ? 'rounded-full border-0 bg-[#596ae8]/10 px-3 py-1 text-xs font-medium text-[#596ae8]'
      : 'cursor-pointer rounded-full border border-[#eef0f4] bg-white px-3 py-1 text-xs text-slate-600 hover:border-[#596ae8]/30';

  return (
    <Modal
      title={t('email.new_send')}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={640}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto', paddingRight: 4 } }}
      closeIcon={
        <span className="cursor-pointer text-sm font-normal text-slate-500 hover:text-slate-700">{t('common.close')}</span>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button className="rounded-lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="primary" className="wb-btn-primary rounded-lg" loading={sending} onClick={submit}>
            {t('chat.send')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 pt-1">
        <section>
          <div className="mb-2 text-sm text-slate-500">{t('email.recipients')}</div>
          <p className="mb-2 mt-0 text-xs leading-relaxed text-slate-400">
            {selectedIds.length === 0
              ? t('invitation.none_selected')
              : t('email.selected_skip', { count: selectedIds.length })}
          </p>
          <Input
            allowClear
            placeholder={t('invitation.search_placeholder')}
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={tabClass(tab === 'mine')} onClick={() => setTab('mine')}>
              {t('invitation.my_talents', { count: mineTalents.length })}
            </button>
            <button type="button" className={tabClass(tab === 'square')} onClick={() => setTab('square')}>
              {t('invitation.search_square')}
            </button>
          </div>
          <div className="mt-3 max-h-[180px] overflow-y-auto rounded-lg border border-[#eef0f4] bg-[#fafbfc] px-2 py-1">
            {listLoading ? (
              <div className="flex justify-center py-6">
                <Spin size="small" />
              </div>
            ) : null}
            {!listLoading && listSource.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-slate-400">{t('invitation.no_match')}</p>
            ) : null}
            {listSource.map((talent) => (
              <label
                key={talent.id}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 hover:bg-white"
              >
                <Checkbox
                  checked={selectedIds.includes(talent.id)}
                  onChange={(event) => toggleTalent(talent, event.target.checked)}
                />
                {talent.avatarUrl ? (
                  <img src={talent.avatarUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-[#596ae8] text-xs font-semibold text-white">
                    {talent.initial}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-slate-800">{talent.name}</span>
                  <span className="block truncate text-xs text-slate-500">{talent.meta}</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section>
          <div className="mb-2 text-sm text-slate-500">{t('invitation.task_name')}</div>
          <Input
            allowClear
            maxLength={255}
            placeholder={t('invitation.task_name_placeholder')}
            value={taskName}
            onChange={(event) => setTaskName(event.target.value)}
          />
        </section>

        <section>
          <div className="mb-2 text-sm text-slate-500">{t('chat.script')}</div>
          {shopId?.trim() ? (
            <Select
              allowClear
              className="w-full"
              placeholder={t('invitation.template_placeholder')}
              loading={templatesLoading}
              value={templateId}
              options={templates
                .filter((item) => item.id != null)
                .map((item) => ({ value: item.id as number, label: item.name?.trim() || String(item.id) }))}
              onChange={setTemplateId}
            />
          ) : (
            <p className="mb-0 text-sm text-slate-400">{t('email.shop_required')}</p>
          )}
        </section>

      </div>
    </Modal>
  );
}
